import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/mock-survey-preparation#article`,
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/mock-survey-preparation`,
  headline: 'How to Prepare for a Mock Survey: A Step-by-Step Approach',
  description: 'Mock surveys are one of the most powerful tools for ensuring your agency is ready for the real thing. Learn how to conduct an effective mock survey and what to do with the results.',
  image: 'https://public.readdy.ai/ai/img_res/819d7094af9100886eee1abc2b8f5350.jpg',
  datePublished: '2026-01-20',
  dateModified: '2026-01-20',
  author: {
    '@type': 'Person',
    name: 'Dr. Osmel Villarreal',
    jobTitle: 'ACHC Certified Consultant, DBA, MBA, MS',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/why-dr-villarreal`,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Advantixx Home Health Accreditation Consultants',
    url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  },
  isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog#webpage` },
  keywords: ['mock survey', 'ACHC survey preparation', 'home health survey', 'accreditation readiness', 'survey simulation'],
  articleSection: 'Survey Prep',
  wordCount: 1700,
};

export default function MockSurveyPreparation() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-mock-survey';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-mock-survey');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-0">
        <div className="relative h-[420px] md:h-[520px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=healthcare%20team%20in%20a%20meeting%20room%20reviewing%20survey%20preparation%20documents%2C%20professional%20setting%20with%20dark%20walls%20and%20warm%20gold%20lighting%2C%20organized%20and%20focused&width=1400&height=600&seq=blog3hero&orientation=landscape"
            alt="Mock Survey Preparation for ACHC Home Health Agencies"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/50 to-[#0d0d0d]/20"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 md:px-10 pb-12">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-[#d4af37] text-[#0d0d0d] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Survey Prep</span>
              <span className="text-[#888] text-xs">January 20, 2026</span>
              <span className="text-[#888] text-xs">•</span>
              <span className="text-[#888] text-xs">7 min read</span>
              <span className="text-[#888] text-xs">•</span>
              <span className="text-[#888] text-xs">By Dr. Osmel Villarreal, DBA</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">
              How to Prepare for a Mock Survey:<br className="hidden md:block" />
              <span className="text-[#d4af37]"> A Step-by-Step Approach</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          <article className="flex-1 min-w-0">
            <p className="text-[#bbb] text-lg leading-relaxed mb-8 border-l-4 border-[#d4af37] pl-6">
              A mock survey is one of the most powerful tools available to home health agencies preparing for ACHC accreditation. It simulates the real survey experience, exposes gaps before they become official findings, and gives your team the confidence to perform under pressure.
            </p>
            <p className="text-[#999] leading-relaxed mb-10">
              But a mock survey is only as valuable as the preparation and follow-through behind it. Here is a step-by-step approach to conducting an effective mock survey that actually moves the needle on your accreditation readiness.
            </p>

            <h2 className="text-2xl font-black text-white mb-5 mt-10">
              <span className="text-[#d4af37]">01.</span> What Is a Mock Survey and Why Does It Matter?
            </h2>
            <p className="text-[#999] leading-relaxed mb-5">
              A mock survey is a simulated ACHC survey conducted by an experienced consultant or internal team before the actual survey takes place. It evaluates your agency against the same standards ACHC surveyors use — including documentation review, staff interviews, clinical record audits, and operational observations.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-search-eye-line', title: 'Identify Gaps', desc: 'Find deficiencies before surveyors do' },
                { icon: 'ri-team-line', title: 'Prepare Staff', desc: 'Build confidence for real surveyor interviews' },
                { icon: 'ri-file-list-3-line', title: 'Validate Docs', desc: 'Confirm all records are complete and compliant' },
              ].map((item, i) => (
                <div key={i} className="bg-[#111] border border-[#1e1e1e] rounded-xl p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-[#d4af37]/10">
                    <i className={`${item.icon} text-[#d4af37] text-lg`}></i>
                  </div>
                  <p className="text-white font-bold text-sm mb-1">{item.title}</p>
                  <p className="text-[#666] text-xs">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-black text-white mb-5 mt-10">
              <span className="text-[#d4af37]">02.</span> Step-by-Step Mock Survey Process
            </h2>

            {[
              { step: 'Step 1: Schedule and Announce the Mock Survey', content: 'Set a specific date for the mock survey and treat it like the real thing. Notify staff in advance so they can prepare — but do not give them so much notice that they only clean up for the event. The goal is to assess your agency\'s everyday state of readiness, not a staged performance.' },
              { step: 'Step 2: Assemble Your Mock Survey Team', content: 'Ideally, your mock survey should be conducted by an external ACHC Certified Consultant who can provide an objective, expert evaluation. If using internal staff, assign team members who are not directly responsible for the areas being reviewed to ensure objectivity.' },
              { step: 'Step 3: Review All ACHC Standards', content: 'Before the mock survey begins, ensure your team has a current copy of the applicable ACHC standards. Map each standard to your agency\'s policies, procedures, and operational practices. This mapping exercise alone often reveals gaps that need to be addressed.' },
              { step: 'Step 4: Conduct the Document Review', content: 'Pull a representative sample of clinical records, personnel files, and administrative documents. Review each against ACHC requirements. Look for missing signatures, incomplete assessments, expired credentials, outdated policies, and documentation inconsistencies.' },
              { step: 'Step 5: Conduct Staff Interviews', content: 'Interview staff at all levels — clinical, administrative, and management. Ask the same types of questions ACHC surveyors ask: How do you handle a patient emergency? What is your infection control protocol? How do you report a complaint? Staff answers reveal whether training has been effective.' },
              { step: 'Step 6: Observe Operations', content: 'Walk through your agency\'s operations as a surveyor would. Observe how staff interact with patients, how documentation is completed in real time, how medications are managed, and how infection control practices are followed. Note any deviations from policy.' },
              { step: 'Step 7: Compile Findings and Prioritize', content: 'After the mock survey, compile all findings into a structured report. Categorize deficiencies by severity — critical (immediate risk to accreditation), significant (likely to result in a finding), and minor (areas for improvement). Prioritize corrective actions accordingly.' },
              { step: 'Step 8: Develop and Implement a Corrective Action Plan', content: 'For each finding, develop a specific corrective action with a responsible party, timeline, and measurable outcome. Implement corrections immediately and document all actions taken. This documentation demonstrates your agency\'s commitment to continuous improvement.' },
              { step: 'Step 9: Conduct a Follow-Up Review', content: 'After implementing corrective actions, conduct a follow-up review to verify that deficiencies have been resolved. Do not assume corrections are complete — verify them with evidence. This follow-up is critical to ensuring your agency is truly ready for the actual survey.' },
            ].map((item, i) => (
              <div key={i} className="mb-8 pl-6 border-l-2 border-[#1e1e1e] hover:border-[#d4af37]/40 transition-colors duration-300">
                <h3 className="text-white font-bold text-base mb-2">{item.step}</h3>
                <p className="text-[#888] text-sm leading-relaxed">{item.content}</p>
              </div>
            ))}

            <h2 className="text-2xl font-black text-white mb-5 mt-12">
              <span className="text-[#d4af37]">03.</span> Common Mock Survey Findings
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {[
                'Incomplete or missing OASIS assessments',
                'Expired staff licenses or certifications',
                'Unsigned or undated physician orders',
                'Missing or outdated emergency preparedness plans',
                'Inadequate infection control documentation',
                'Incomplete or missing care plans',
                'Lack of documented QAPI activities',
                'Staff unable to articulate agency policies',
                'Missing patient rights documentation',
                'Incomplete personnel files',
              ].map((finding, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#111] rounded-lg p-4 border border-[#1e1e1e]">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-alert-line text-[#d4af37] text-sm"></i>
                  </div>
                  <span className="text-[#888] text-sm leading-relaxed">{finding}</span>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-8 text-center mt-10">
              <h3 className="text-white font-black text-xl mb-3">Let Advantixx Conduct Your Mock Survey</h3>
              <p className="text-[#888] text-sm mb-6 leading-relaxed">
                Our ACHC Certified Consultant conducts comprehensive mock surveys that mirror the real ACHC survey experience — giving your agency the clearest picture of where you stand and exactly what to fix.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Schedule a Mock Survey
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-28 flex flex-col gap-6">
              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-4">About the Author</p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-[#d4af37] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Dr. Osmel Villarreal</p>
                    <p className="text-[#666] text-xs">DBA, MBA, MS</p>
                  </div>
                </div>
                <p className="text-[#777] text-xs leading-relaxed">
                  ACHC Certified Consultant with hands-on experience conducting mock surveys and preparing agencies for successful accreditation.
                </p>
                <Link to="/why-dr-villarreal" className="mt-4 flex items-center gap-1.5 text-[#d4af37] text-xs font-semibold hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer">
                  Learn More <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">Related Articles</p>
                <div className="flex flex-col gap-5">
                  {[
                    { title: 'Complete ACHC Accreditation Guide', path: '/blog/achc-accreditation-guide' },
                    { title: 'Top 10 Compliance Tips for 2026', path: '/blog/home-health-compliance-tips' },
                    { title: 'ACHC Accreditation FAQ', path: '/faq' },
                  ].map((post, i) => (
                    <Link key={i} to={post.path} className="group cursor-pointer">
                      <p className="text-[#aaa] text-sm leading-snug group-hover:text-[#d4af37] transition-colors duration-200">{post.title}</p>
                      <span className="text-[#555] text-xs mt-1 flex items-center gap-1 group-hover:gap-2 transition-all duration-200">Read <i className="ri-arrow-right-line text-xs"></i></span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#d4af37]/15 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-6 text-center">
                <i className="ri-calendar-check-line text-[#d4af37] text-3xl mb-3 block"></i>
                <p className="text-white font-bold text-sm mb-2">Free Consultation</p>
                <p className="text-[#777] text-xs mb-4 leading-relaxed">Get your personalized mock survey and accreditation roadmap.</p>
                <Link to="/services" className="block bg-[#d4af37] text-[#0d0d0d] font-bold text-xs px-5 py-2.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  Book Now
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-10 pb-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-[#888] hover:text-[#d4af37] text-sm transition-colors duration-200 cursor-pointer">
          <i className="ri-arrow-left-line"></i>
          Back to All Articles
        </Link>
      </div>

      <Footer />
    </div>
  );
}
